import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Common ISO 639-1 language codes.
 * Feel free to expand this list to your needs.
 */
const LANGUAGE_CODES = [
  "en","es","fr","de","it","pt","ru","zh","ja","ko","ar","hi","bn","tr","fa","ur",
  "pl","uk","nl","sv","no","da","fi","el","he","id","ms","th","vi","cs","ro","hu",
  "sr","hr","bg","sk","sl","et","lv","lt","sw","af","zu","xh","am","ne","si","ta",
  "te","ml","kn","gu","pa","mr","km","lo","my","mn","ka","az","hy","kk","uz","tk"
];

/** Get localized language name; falls back to code if Intl DisplayNames not supported */
function getLanguageName(code, locale) {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "language" });
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    function listener(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    }
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export default function LanguageSelect({
  value = "en",
  onChange,
  locale = "en",
  label = "Language",
  placeholder = "Select a language",
  id = "language-select",
  disabled = false,
  fullWidth = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useOnClickOutside(rootRef, () => setOpen(false));

  // Build language list (label + code), sorted by localized label
  const languages = useMemo(() => {
    const arr = LANGUAGE_CODES.map((code) => ({
      code,
      label: getLanguageName(code, locale),
    }));
    return arr.sort((a, b) => a.label.localeCompare(b.label));
  }, [locale]);

  // Filter by query (matches display label or code)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter(
      (l) => l.label.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)
    );
  }, [query, languages]);

  // Keep activeIndex in range when the filtered list changes
  useEffect(() => {
    setActiveIndex((i) => Math.min(Math.max(i, 0), Math.max(filtered.length - 1, 0)));
  }, [filtered.length]);

  const current = languages.find((l) => l.code === value) || languages[0];

  const selectAt = (i) => {
    const item = filtered[i];
    if (!item) return;
    onChange?.(item.code);
    setOpen(false);
    setQuery("");
  };

  // keyboard handlers
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      selectAt(activeIndex);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      e.preventDefault();
    }
  };

  // Keep the active option in view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  return (
    <div
      ref={rootRef}
      style={{ display: "inline-flex", flexDirection: "column", gap: 8, width: "100%" }}
    >
      {/* If you want a visible label, uncomment below
      <label htmlFor={id} style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
        {label}
      </label> */}

      {/* Button / Field */}
      <div
      className="lang-select-box"
        role="combobox"
        aria-expanded={open}
        aria-owns={`${id}-listbox`}
        aria-haspopup="listbox"
        aria-controls={`${id}-listbox`}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={() => !disabled && setOpen((o) => !o)}
        id={id}
        style={{
          // position: "relative",
          // display: "inline-flex",
          // alignItems: "center",
          // height: 38,
          // border: "1px solid #e5e7eb",
          // borderRadius: 10,
          // paddingLeft: 12,
          // paddingRight: 28,
          // background: "#fff",
          // minWidth: 160,
          width: fullWidth ? "100%" : undefined,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ fontSize: 14, color: "#111827" }}>
          {current ? current.label : placeholder}
        </span>

        {/* Chevron */}
        <svg viewBox="0 0 24 24" style={{ position: "absolute", right: 6, width: 18, height: 18, opacity: 0.7 }}>
          <path d="M7 9l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div
        className="lang-dropdown"
          // style={{
          //   position: "absolute",
          //   marginTop: 44,
          //   background: "#fff",
          //   border: "1px solid #e5e7eb",
          //   borderRadius: 10,
          //   boxShadow: "0 6px 24px rgba(0,0,0,.08)",
          //   width: fullWidth ? "100%" : 296,
          //   maxHeight: 300,
          //   overflow: "hidden",
          //   zIndex: 50,
          // }}
        >
          {/* Search */}
          <div style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                width: "92%",
                height: 32,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                padding: "0 10px",
                outline: "none",
                fontSize: 14,
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
                  e.preventDefault();
                }
              }}
            />
          </div>

          {/* List */}
          <ul
            id={`${id}-listbox`}
            role="listbox"
            ref={listRef}
            style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 270, overflowY: "auto" }}
          >
            {filtered.map((l, i) => {
              const selected = l.code === value;
              const active = i === activeIndex;
              return (
                <li
                  key={l.code}
                  role="option"
                  aria-selected={selected}
                  data-index={i}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectAt(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    background: active ? "#f3f4f6" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 14, color: "#111827", flex: 1 }}>{l.label}</span>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{l.code}</span>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li style={{ padding: 12, color: "#6b7280", fontSize: 14 }}>No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
